// @ts-nocheck
// Mongo-backed admin auth context (replaces Clerk). Auth rides on an httpOnly
// cookie (`atoken`); this provider bootstraps from GET /admins/auth/me and
// exposes login/logout/switchOrg + the current admin & active organization.
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { axios } from '../store/axios';

const baseUrl = `${process.env.API_URL}/api/v1`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const applyMe = (result) => {
    const a = (result && result.admin) || null;
    setAdmin(a);
    const fallbackOrg = a && a.organizations && a.organizations[0];
    setActiveOrgId(
      (result && result.activeOrganizationId) ||
      (fallbackOrg && String(fallbackOrg._id || fallbackOrg)) || null,
    );
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/admins/auth/me`);
      applyMe(data && data.result);
      return (data && data.result && data.result.admin) || null;
    } catch (e) {
      setAdmin(null);
      setActiveOrgId(null);
      return null;
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${baseUrl}/admins/auth/login`, { email, password });
    if (!data || data.status === 0) throw new Error((data && data.error) || 'Login failed');
    // Gate sign-in on a real /me so `isSignedIn` only flips true when the session
    // cookie actually authenticates. Signing in from the POST body instead would
    // let a user whose `atoken` didn't stick (stale duplicate, blocked cookie)
    // into the app, where every request 401s and the layout loops. If the cookie
    // failed, surface an explicit error rather than silently bouncing to /sign-in.
    const me = await refresh();
    if (!me) throw new Error('Signed in, but your session could not be started. Please make sure cookies are enabled, then try again.');
    return data.result;
  }, [refresh]);

  // Acquisition attribution (funnel analytics 2026-09-14): SignUpPage stashes
  // first-touch ad/CTA params; both auth-creation paths attach them so the
  // server can stamp admin.acquisition. Never blocks auth; cleared on success.
  const readAcquisition = () => {
    try {
      const raw = localStorage.getItem('staty-acquisition');
      return raw ? JSON.parse(raw) : undefined;
    } catch { return undefined; }
  };
  const clearAcquisition = () => {
    try { localStorage.removeItem('staty-acquisition'); } catch { /* ignore */ }
  };

  const register = useCallback(async (payload) => {
    const acquisition = readAcquisition();
    const { data } = await axios.post(`${baseUrl}/admins/auth/register`, acquisition ? { ...payload, acquisition } : payload);
    if (!data || data.status === 0) throw new Error((data && data.error) || 'Registration failed');
    clearAcquisition();
    await refresh();
    return data.result;
  }, [refresh]);

  // Google Identity Services credential → session. Same cookie-gated /me
  // check as `login` so isSignedIn never flips without a working session.
  const loginWithGoogle = useCallback(async (credential) => {
    const acquisition = readAcquisition();
    const { data } = await axios.post(`${baseUrl}/admins/auth/google`, acquisition ? { credential, acquisition } : { credential });
    if (!data || data.status === 0) throw new Error((data && data.error) || 'Google sign-in failed');
    clearAcquisition();
    const me = await refresh();
    if (!me) throw new Error('Signed in, but your session could not be started. Please make sure cookies are enabled, then try again.');
    return data.result;
  }, [refresh]);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await axios.post(`${baseUrl}/admins/auth/forgot`, { email });
    return data && data.result;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const { data } = await axios.post(`${baseUrl}/admins/auth/reset`, { token, password });
    if (!data || data.status === 0) throw new Error((data && data.error) || 'Reset failed');
    await refresh();
    return data.result;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await axios.post(`${baseUrl}/admins/auth/logout`, {}); } catch (e) { /* ignore */ }
    setAdmin(null);
    setActiveOrgId(null);
    window.location.href = '/sign-in';
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    const { data } = await axios.post(`${baseUrl}/admins/auth/switch-org`, { organizationId: orgId });
    if (!data || data.status === 0) throw new Error((data && data.error) || 'Switch failed');
    // Re-fetch /me: `activeOrg` is derived by finding orgId in the boot-time
    // `admin.organizations` snapshot, so switching to an org created AFTER
    // boot (the create-org flow) otherwise resolves to organizations[0] — or
    // null for a first org — stranding every activeOrg consumer (banner page,
    // membership guards) on the wrong/no org.
    await refresh();
    setActiveOrgId(String(orgId));
    return data.result;
  }, [refresh]);

  const organizations = (admin && admin.organizations) || [];
  const activeOrg = organizations.find((o) => String(o._id || o) === String(activeOrgId)) || organizations[0] || null;
  const isSuperAdmin = !!(admin && admin.provider && admin.provider.role === 'org:admin');

  // Memoize so consumers get a STABLE context value (Clerk's hooks were stable);
  // otherwise `useEffect(..., [user/organization])` call sites loop infinitely.
  const value = useMemo(() => ({
    admin, activeOrg, activeOrgId, organizations,
    isLoaded, isSignedIn: !!admin, isSuperAdmin,
    login, logout, switchOrg, refresh, register, loginWithGoogle, forgotPassword, resetPassword,
  }), [admin, activeOrgId, isLoaded, isSuperAdmin, login, logout, switchOrg, refresh, register, loginWithGoogle, forgotPassword, resetPassword]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AuthProvider');
  return ctx;
}
